import { CheckoutContext } from '@/context/CheckoutContext';
import { Elements, ExpressCheckoutElement, useElements } from '@stripe/react-stripe-js';
import type {
  StripeExpressCheckoutElementClickEvent,
  StripeExpressCheckoutElementConfirmEvent,
} from '@stripe/stripe-js';
import { useContext, useState } from 'react';
import { SkeletonLoader } from '../Common/SkeletonLoader';
import { LINK_PAYMENT_CONFIG } from '@/constants/stripe';
import { InputTaxComponent } from '../InputTaxComponent/InputTaxComponent';
import { useTranslate } from '@/hooks/useTranslate';
import { Loader } from '../Common/Loader';
import { PaymentMethodEnum } from '@/types/common';
import { useStripeInitialize } from '@/hooks/useStripeInitialize';
import { getZipCodeErrorMessageKey } from '@/utils/zipCode';
import { trackAndNotifyPaymentClick } from '@/utils/paymentClickEvents';

interface LinkButtonUIProps {
  isLoading: boolean;
  onReady: () => void;
  onConfirm: (event: StripeExpressCheckoutElementConfirmEvent) => void;
}

const LinkButtonUI = ({ isLoading, onReady, onConfirm }: LinkButtonUIProps) => {
  const context = useContext(CheckoutContext);
  const {
    actions: { setError },
    payment: { isFormValid },
    customer,
  } = context;
  const { t } = useTranslate();

  const handleClick = (event: StripeExpressCheckoutElementClickEvent) => {
    if (isFormValid) {
      trackAndNotifyPaymentClick(context, PaymentMethodEnum.LINK);
      event.resolve({ emailRequired: true });
    } else {
      const errorMessageKey = getZipCodeErrorMessageKey(customer);
      setError(t(errorMessageKey));
      event.reject();
    }
  };

  return (
    <div className="flex flex-col w-full min-h-11">
      <div className={`transition-opacity duration-500 ${!isLoading && 'hidden'}`}>
        <SkeletonLoader />
      </div>
      <div className={`transition-opacity duration-500 ${isLoading && 'hidden'}`}>
        <ExpressCheckoutElement
          onClick={handleClick}
          onConfirm={onConfirm}
          onReady={onReady}
          options={LINK_PAYMENT_CONFIG}
        />
      </div>
    </div>
  );
};

const LinkButtonContent = () => {
  const {
    actions: { runPayment, setError },
  } = useContext(CheckoutContext);
  const elements = useElements();
  const [isLoading, setIsLoading] = useState(true);
  const { t } = useTranslate();

  const handleReady = () => {
    setTimeout(() => setIsLoading(false), 500);
  };

  const handleConfirm = async (event: StripeExpressCheckoutElementConfirmEvent) => {
    try {
      await runPayment({ event, elements: elements! });
    } catch {
      setError(t('somethingWentWrong'));
    }
  };

  return <LinkButtonUI isLoading={isLoading} onReady={handleReady} onConfirm={handleConfirm} />;
};

export const LinkPayForm = () => {
  const { pricing } = useContext(CheckoutContext);
  const { stripe, isLoading: isStripeLoading } = useStripeInitialize({ paymentMethod: PaymentMethodEnum.LINK });

  if (isStripeLoading || !stripe) {
    return <Loader />;
  }
  return (
    <div className="flex flex-col gap-4">
      <Elements
        stripe={stripe}
        options={{
          mode: 'payment',
          amount: Math.round(pricing.priceBreakdown.priceTotal),
          currency: pricing.currencyCode.toLowerCase(),
          loader: 'never',
          paymentMethodCreation: 'manual',
        }}
      >
        <LinkButtonContent />
      </Elements>
      <InputTaxComponent />
    </div>
  );
};
